import React from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Iconify from "../../iconify/Iconify";
import {
  calculateBookingPricePreview,
  getPackageIncludedSessionIds,
  getSortedSessions,
  resolveSessionId,
  validateBookingPackageRulesRestPricing,
} from "../booking/bookingPricingUtils";

const createRuleId = () =>
  `rule-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createEmptyRule = () => ({
  ruleId: createRuleId(),
  label: "",
  type: "fixed",
  amount: "",
  memberDiscount: "",
  sessionIds: [],
  minSessions: "",
  isDefault: false,
});

const formatCurrency = (value) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
    Number(value) || 0
  );

const RULE_TYPE_DESCRIPTIONS = {
  fixed:
    "Ein fixer Gesamtpreis pro Teilnehmer, unabhängig von der Anzahl gebuchter Sessions. Ideal wenn die Teilnahmegebühr immer gleich ist (z.\u00A0B. Camp-Pauschale).",
  per_session:
    "Der Preis wird pro ausgewählter Session berechnet. Beispiel: Tagesticket 25\u00A0\u20AC \u00D7 3 gebuchte Tage = 75\u00A0\u20AC.",
  package:
    "Ein kombinierbarer Bündelpreis: entweder für verknüpfte Sessions plus Zusatz-Sessions oder als Schwellenrabatt ab Mindestanzahl (mehrfach anwendbar).",
};

// Determine the package mode from rule data
const getPackageMode = (rule) => {
  if (Number(rule?.minSessions) >= 1) return "minSessions";
  return "linkedSessions";
};

export default function StepPricingBooking({ formData, errors, onChange }) {
  const pricingRules = React.useMemo(
    () => (Array.isArray(formData.pricingRules) ? formData.pricingRules : []),
    [formData.pricingRules],
  );
  const sessions = React.useMemo(
    () => (Array.isArray(formData.sessions) ? formData.sessions : []),
    [formData.sessions],
  );
  const sortedSessions = getSortedSessions(sessions);
  const allSessionIds = sortedSessions
    .map((session, index) => resolveSessionId(session, `session-${index}`))
    .filter(Boolean);
  const registrationConfig = formData.registrationConfig || {};
  const discountRules = registrationConfig.discountRules || {};
  const siblingRule = discountRules.siblingDiscount || {};
  const earlyBirdRule = discountRules.earlyBirdDiscount || {};

  const updatePricingRules = (nextRules) => {
    onChange("pricingRules", nextRules);
  };

  const updateRule = (index, patch) => {
    const next = pricingRules.map((rule, ruleIndex) =>
      ruleIndex === index ? { ...rule, ...patch } : rule
    );
    updatePricingRules(next);
  };

  const addRule = () => {
    updatePricingRules([...pricingRules, createEmptyRule()]);
  };

  const removeRule = (index) => {
    updatePricingRules(pricingRules.filter((_, ruleIndex) => ruleIndex !== index));
  };

  const toggleDefaultRule = (index, checked) => {
    const next = pricingRules.map((rule, ruleIndex) => ({
      ...rule,
      isDefault: checked ? ruleIndex === index : false,
    }));
    updatePricingRules(next);
  };

  const updateDiscountRules = (patch) => {
    onChange("registrationConfig", {
      ...registrationConfig,
      discountRules: {
        siblingDiscount: {
          enabled: Boolean(siblingRule.enabled),
          percentage: siblingRule.percentage ?? "",
          ...(patch.siblingDiscount || {}),
        },
        earlyBirdDiscount: {
          enabled: Boolean(earlyBirdRule.enabled),
          type: earlyBirdRule.type === "fixed" ? "fixed" : "percentage",
          percentage: earlyBirdRule.percentage ?? "",
          amount: earlyBirdRule.amount ?? "",
          deadline: earlyBirdRule.deadline || "",
          ...(patch.earlyBirdDiscount || {}),
        },
      },
    });
  };

  const defaultRule = pricingRules.find((rule) => Boolean(rule.isDefault)) || pricingRules[0];
  const packageRuleCoverageValidation = validateBookingPackageRulesRestPricing({
    pricingRules,
    sessions: sortedSessions,
  });

  const previewParticipants = 2;
  const previewSelectedSessionIds = React.useMemo(() => {
    if (!defaultRule) return [];

    if (defaultRule.type === "per_session") {
      const linkedSessionIds = (Array.isArray(defaultRule.sessionIds)
        ? defaultRule.sessionIds
        : []
      ).filter((sessionId) => allSessionIds.includes(sessionId));
      if (linkedSessionIds.length > 0) {
        return linkedSessionIds.slice(0, Math.max(1, linkedSessionIds.length));
      }
      return allSessionIds.length > 0 ? [allSessionIds[0]] : [];
    }

    if (defaultRule.type === "package") {
      const minSessions = Number(defaultRule.minSessions) || 0;
      if (minSessions > 0) {
        const targetCount = Math.max(1, minSessions + 1);
        return allSessionIds.slice(0, Math.min(targetCount, allSessionIds.length));
      }

      const linkedSessionIds = getPackageIncludedSessionIds(defaultRule, sortedSessions);
      if (linkedSessionIds.length > 0) {
        const linkedSet = new Set(linkedSessionIds);
        const additionalSessionId = allSessionIds.find(
          (sessionId) => !linkedSet.has(sessionId),
        );
        return additionalSessionId
          ? [...linkedSessionIds, additionalSessionId]
          : linkedSessionIds;
      }
    }

    return [];
  }, [allSessionIds, defaultRule, sortedSessions]);

  const previewPricing = React.useMemo(() => {
    if (!defaultRule) return null;
    return calculateBookingPricePreview({
      event: {
        pricingRules,
        sessions: sortedSessions,
        registrationConfig: {
          discountRules: {
            siblingDiscount: {
              enabled: Boolean(siblingRule.enabled),
              percentage: siblingRule.percentage ?? 0,
            },
            earlyBirdDiscount: {
              enabled: Boolean(earlyBirdRule.enabled),
              type: earlyBirdRule.type === "fixed" ? "fixed" : "percentage",
              percentage: earlyBirdRule.percentage ?? 0,
              amount: earlyBirdRule.amount ?? 0,
              deadline: earlyBirdRule.deadline || "",
            },
          },
        },
      },
      selectedPricingRuleId: defaultRule.ruleId,
      selectedSessionIds: previewSelectedSessionIds,
      participantCount: previewParticipants,
      memberParticipantCount: previewParticipants,
      bookingDate: new Date(),
    });
  }, [
    defaultRule,
    earlyBirdRule.amount,
    earlyBirdRule.deadline,
    earlyBirdRule.enabled,
    earlyBirdRule.percentage,
    earlyBirdRule.type,
    previewSelectedSessionIds,
    pricingRules,
    siblingRule.enabled,
    siblingRule.percentage,
    sortedSessions,
  ]);

  const earlyBirdDiscountType =
    earlyBirdRule.type === "fixed" ? "fixed" : "percentage";
  const earlyBirdDiscountFixedAmount = Math.max(0, Number(earlyBirdRule.amount || 0));

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Preise & Rabatte</Typography>
        <Typography variant="body2" color="text.secondary">
          Definieren Sie Preisregeln und Rabattmechaniken für Buchungen.
        </Typography>
      </Box>

      {errors.pricingRules ? <Alert severity="error">{errors.pricingRules}</Alert> : null}
      {errors.discountRules ? <Alert severity="error">{errors.discountRules}</Alert> : null}
      {!packageRuleCoverageValidation.isValid ? (
        <Alert severity="warning">{packageRuleCoverageValidation.message}</Alert>
      ) : null}

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Preisregeln</Typography>

        {pricingRules.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{ p: 2.5, borderStyle: "dashed", borderColor: "divider" }}
          >
            <Typography variant="body2" color="text.secondary">
              Noch keine Preisregel vorhanden.
            </Typography>
          </Paper>
        ) : (
          pricingRules.map((rule, index) => {
            const ruleKey = rule.ruleId || rule._id || `rule-${index}`;
            const linkedSessions = Array.isArray(rule.sessionIds) ? rule.sessionIds : [];
            const isPackage = rule.type === "package";
            const packageMode = isPackage ? getPackageMode(rule) : null;

            return (
              <Paper key={ruleKey} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle2">Regel {index + 1}</Typography>
                      {rule.isDefault ? (
                        <Chip size="small" color="secondary" label="Standard" />
                      ) : null}
                    </Stack>
                    <Tooltip title="Regel entfernen">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeRule(index)}
                      >
                        <Iconify icon="solar:trash-bin-trash-bold" width={16} />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <TextField
                      label="Bezeichnung"
                      value={rule.label || ""}
                      onChange={(event) =>
                        updateRule(index, {
                          label: event.target.value,
                        })
                      }
                      required
                      fullWidth
                    />

                    <TextField
                      label="Typ"
                      select
                      value={rule.type || "fixed"}
                      onChange={(event) => {
                        const nextType = event.target.value;
                        updateRule(index, {
                          type: nextType,
                          sessionIds:
                            nextType === "fixed"
                              ? []
                              : Array.isArray(rule.sessionIds)
                                ? rule.sessionIds
                                : [],
                          minSessions: nextType === "package" ? rule.minSessions || "" : "",
                        });
                      }}
                      sx={{ minWidth: { xs: "100%", md: 180 } }}
                    >
                      <MenuItem value="fixed">Festpreis</MenuItem>
                      <MenuItem value="per_session">Pro Session</MenuItem>
                      <MenuItem value="package">Paket</MenuItem>
                    </TextField>
                  </Stack>

                  {/* Explanation text for the selected rule type */}
                  <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                    <Typography variant="caption">
                      {RULE_TYPE_DESCRIPTIONS[rule.type] || ""}
                    </Typography>
                  </Alert>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <TextField
                      label="Betrag"
                      type="number"
                      value={rule.amount ?? ""}
                      onChange={(event) =>
                        updateRule(index, {
                          amount: event.target.value,
                        })
                      }
                      inputProps={{ min: 0, step: "0.01" }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">EUR</InputAdornment>,
                      }}
                      required
                      fullWidth
                    />

                    <TextField
                      label="Mitgliederrabatt"
                      type="number"
                      value={rule.memberDiscount ?? ""}
                      onChange={(event) =>
                        updateRule(index, {
                          memberDiscount: event.target.value,
                        })
                      }
                      inputProps={{ min: 0, max: 100, step: "1" }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      fullWidth
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(rule.isDefault)}
                          onChange={(event) =>
                            toggleDefaultRule(index, event.target.checked)
                          }
                          color="secondary"
                        />
                      }
                      label="Standard"
                    />
                  </Stack>

                  {/* per_session: linked sessions picker */}
                  {rule.type === "per_session" && (
                    <TextField
                      label="Verknüpfte Sessions"
                      select
                      SelectProps={{ multiple: true }}
                      value={linkedSessions}
                      onChange={(event) =>
                        updateRule(index, {
                          sessionIds: event.target.value,
                        })
                      }
                      helperText="Relevante Sessions für diese Preisregel auswählen."
                      fullWidth
                    >
                      {sessions.map((session, sessionIndex) => {
                        const sessionId =
                          session.sessionId || session._id || `session-${sessionIndex}`;
                        return (
                          <MenuItem key={sessionId} value={sessionId}>
                            {session.label || `Session ${sessionIndex + 1}`}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  )}

                  {/* package: toggle between linked sessions and minSessions */}
                  {isPackage && (
                    <Stack spacing={1.5}>
                      <RadioGroup
                        row
                        value={packageMode}
                        onChange={(event) => {
                          if (event.target.value === "minSessions") {
                            updateRule(index, {
                              sessionIds: [],
                              minSessions: rule.minSessions || 2,
                            });
                          } else {
                            updateRule(index, {
                              minSessions: "",
                              sessionIds: Array.isArray(rule.sessionIds) ? rule.sessionIds : [],
                            });
                          }
                        }}
                      >
                        <FormControlLabel
                          value="linkedSessions"
                          control={<Radio size="small" />}
                          label={
                            <Typography variant="body2">Bestimmte Sessions verknüpfen</Typography>
                          }
                        />
                        <FormControlLabel
                          value="minSessions"
                          control={<Radio size="small" />}
                          label={
                            <Typography variant="body2">Ab Mindestanzahl Sessions</Typography>
                          }
                        />
                      </RadioGroup>

                      <Collapse in={packageMode === "linkedSessions"}>
                        <TextField
                          label="Verknüpfte Sessions"
                          select
                          SelectProps={{ multiple: true }}
                          value={linkedSessions}
                          onChange={(event) =>
                            updateRule(index, {
                              sessionIds: event.target.value,
                            })
                          }
                          helperText='Verknüpfte Sessions sind im Paket enthalten. Nicht verknüpfte Sessions bleiben zusätzlich auswählbar und werden über "Pro Session"-Regeln berechnet.'
                          fullWidth
                        >
                          {sessions.map((session, sessionIndex) => {
                            const sessionId =
                              session.sessionId || session._id || `session-${sessionIndex}`;
                            return (
                              <MenuItem key={sessionId} value={sessionId}>
                                {session.label || `Session ${sessionIndex + 1}`}
                              </MenuItem>
                            );
                          })}
                        </TextField>
                      </Collapse>

                      <Collapse in={packageMode === "minSessions"}>
                        <TextField
                          label="Mindestanzahl Sessions"
                          type="number"
                          value={rule.minSessions ?? ""}
                          onChange={(event) =>
                            updateRule(index, {
                              minSessions: event.target.value,
                            })
                          }
                          inputProps={{ min: 1, step: 1 }}
                          helperText='Schwellenrabatt: Pakete werden mehrfach angewendet (floor(Auswahl / Mindestanzahl)); Rest-Sessions werden über "Pro Session"-Regeln bepreist.'
                          sx={{ maxWidth: 300 }}
                        />
                      </Collapse>
                    </Stack>
                  )}
                </Stack>
              </Paper>
            );
          })
        )}

        <Box>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="solar:add-circle-bold" width={18} />}
            onClick={addRule}
          >
            Preisregel hinzufügen
          </Button>
        </Box>
      </Stack>

      <Divider />

      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Rabattregeln</Typography>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(siblingRule.enabled)}
                  onChange={(event) =>
                    updateDiscountRules({
                      siblingDiscount: {
                        enabled: event.target.checked,
                      },
                    })
                  }
                  color="secondary"
                />
              }
              label="Geschwisterrabatt aktiv"
            />

            {siblingRule.enabled ? (
              <TextField
                label="Geschwisterrabatt"
                type="number"
                value={siblingRule.percentage ?? ""}
                onChange={(event) =>
                  updateDiscountRules({
                    siblingDiscount: {
                      percentage: event.target.value,
                    },
                  })
                }
                inputProps={{ min: 0, max: 100, step: "1" }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                helperText="Wird ab dem 2. Teilnehmer derselben Buchung gewährt."
                fullWidth
              />
            ) : null}

            <Divider />

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(earlyBirdRule.enabled)}
                  onChange={(event) =>
                    updateDiscountRules({
                      earlyBirdDiscount: {
                        enabled: event.target.checked,
                      },
                    })
                  }
                  color="secondary"
                />
              }
              label="Frühbucherrabatt aktiv"
            />

            {earlyBirdRule.enabled ? (
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                <TextField
                  label="Rabatttyp"
                  select
                  value={earlyBirdDiscountType}
                  onChange={(event) =>
                    updateDiscountRules({
                      earlyBirdDiscount: {
                        type: event.target.value === "fixed" ? "fixed" : "percentage",
                      },
                    })
                  }
                  sx={{ minWidth: { xs: "100%", md: 180 } }}
                >
                  <MenuItem value="percentage">Prozent</MenuItem>
                  <MenuItem value="fixed">Fixbetrag</MenuItem>
                </TextField>

                <TextField
                  label={
                    earlyBirdDiscountType === "fixed"
                      ? "Frühbucherrabatt (Fixbetrag)"
                      : "Frühbucherrabatt"
                  }
                  type="number"
                  value={
                    earlyBirdDiscountType === "fixed"
                      ? earlyBirdRule.amount ?? ""
                      : earlyBirdRule.percentage ?? ""
                  }
                  onChange={(event) =>
                    updateDiscountRules({
                      earlyBirdDiscount: {
                        ...(earlyBirdDiscountType === "fixed"
                          ? { amount: event.target.value }
                          : { percentage: event.target.value }),
                      },
                    })
                  }
                  inputProps={
                    earlyBirdDiscountType === "fixed"
                      ? { min: 0, step: "0.01" }
                      : { min: 0, max: 100, step: "1" }
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {earlyBirdDiscountType === "fixed" ? "EUR" : "%"}
                      </InputAdornment>
                    ),
                  }}
                  helperText="Gilt für Buchungen vor dem Stichtag."
                  fullWidth
                />

                <TextField
                  label="Frühbucher-Stichtag"
                  type="datetime-local"
                  value={earlyBirdRule.deadline || ""}
                  onChange={(event) =>
                    updateDiscountRules({
                      earlyBirdDiscount: {
                        deadline: event.target.value,
                      },
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>
            ) : null}
          </Stack>
        </Paper>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Preisvorschau (Beispiel)
        </Typography>
        {!defaultRule ? (
          <Typography variant="body2" color="text.secondary">
            Bitte mindestens eine Preisregel anlegen, um die Vorschau zu sehen.
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Regel
              </Typography>
              <Typography variant="body2">{defaultRule.label || "Ohne Titel"}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Beispiel-Sessions
              </Typography>
              <Typography variant="body2">{previewSelectedSessionIds.length}</Typography>
            </Stack>
            {defaultRule.type === "package" ? (
              <>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Paketanteile (pro Teilnehmer)
                  </Typography>
                  <Typography variant="body2">
                    {previewPricing?.packageCount || 0} x{" "}
                    {formatCurrency(previewPricing?.packageUnitAmount || 0)}
                  </Typography>
                </Stack>
                {previewPricing?.packageMode === "min_sessions" && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Rest-Sessions (pro Teilnehmer)
                    </Typography>
                    <Typography variant="body2">
                      {formatCurrency(previewPricing?.remainderUnitAmount || 0)}
                    </Typography>
                  </Stack>
                )}
                {previewPricing?.packageMode === "linked_sessions" && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Zusatz-Sessions (pro Teilnehmer)
                    </Typography>
                    <Typography variant="body2">
                      {formatCurrency(previewPricing?.additionalSessionsUnitAmount || 0)}
                    </Typography>
                  </Stack>
                )}
              </>
            ) : null}
            {previewPricing?.calculationError ? (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                {previewPricing.calculationError}
              </Alert>
            ) : null}
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Basis (pro Teilnehmer)
              </Typography>
              <Typography variant="body2">
                {formatCurrency(previewPricing?.baseUnitPrice || 0)}
              </Typography>
            </Stack>
            {Number(defaultRule.memberDiscount || 0) > 0 && previewPricing ? (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Mitgliederrabatt ({Number(defaultRule.memberDiscount || 0)}%)
                </Typography>
                <Typography variant="body2" color="success.main">
                  - {formatCurrency(previewPricing.memberDiscountAmount)}
                </Typography>
              </Stack>
            ) : null}
            {Boolean(siblingRule.enabled) && previewPricing ? (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Geschwisterrabatt ({Number(siblingRule.percentage || 0)}%)
                </Typography>
                <Typography variant="body2" color="success.main">
                  - {formatCurrency(previewPricing.siblingDiscountAmount)}
                </Typography>
              </Stack>
            ) : null}
            {Boolean(earlyBirdRule.enabled) && previewPricing ? (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  {earlyBirdDiscountType === "fixed"
                    ? `Frühbucherrabatt (${formatCurrency(earlyBirdDiscountFixedAmount)})`
                    : `Frühbucherrabatt (${Number(earlyBirdRule.percentage || 0)}%)`}
                </Typography>
                <Typography variant="body2" color="success.main">
                  - {formatCurrency(previewPricing.earlyBirdDiscountAmount)}
                </Typography>
              </Stack>
            ) : null}
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" fontWeight={700}>
                Gesamtbetrag (2 Teilnehmer)
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {formatCurrency(previewPricing?.totalAmount || 0)}
              </Typography>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}

StepPricingBooking.propTypes = {
  formData: PropTypes.shape({
    sessions: PropTypes.array,
    pricingRules: PropTypes.arrayOf(
      PropTypes.shape({
        ruleId: PropTypes.string,
        _id: PropTypes.string,
        label: PropTypes.string,
        type: PropTypes.oneOf(["fixed", "per_session", "package"]),
        amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        memberDiscount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        sessionIds: PropTypes.arrayOf(PropTypes.string),
        minSessions: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        isDefault: PropTypes.bool,
      })
    ),
    registrationConfig: PropTypes.shape({
      discountRules: PropTypes.shape({
        siblingDiscount: PropTypes.shape({
          enabled: PropTypes.bool,
          percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        earlyBirdDiscount: PropTypes.shape({
          enabled: PropTypes.bool,
          type: PropTypes.oneOf(["percentage", "fixed"]),
          percentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          deadline: PropTypes.string,
        }),
      }),
    }),
  }).isRequired,
  errors: PropTypes.shape({
    pricingRules: PropTypes.string,
    discountRules: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};
